/**
 * A seção Mesas da administração (docs/spec/admin.md): todas as mesas do servidor, com o dono, quem já
 * entrou, quem está agora, os arquivos e a última mensagem. Pela busca acha a mesa ou o dono; o "⋯" (ou
 * o botão direito, ou segurar o dedo) abre os detalhes do dono ou apaga a mesa, com o nome digitado.
 */
import { useEffect, useRef, useState } from 'react';
import { api } from '../api.js';
import { formatBytes } from '../bytes.js';
import { useT } from '../i18n/index.js';
import type { AdminRoomRow, AdminRoomSort } from '../shared.js';
import { describeError, useApp } from '../store.js';
import { ContextMenu, type MenuAnchor, type MenuEntry } from '../ui/ContextMenu.js';
import { Button, ComboBox, Spinner } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { EmptyState } from '../ui/EmptyState.js';
import { askText } from '../ui/prompt.js';
import { toast } from '../ui/Toaster.js';
import { useLongPress } from '../ui/touch.js';
import { useDateText } from './AdminUsers.js';

const SORTS: readonly AdminRoomSort[] = ['recent', 'name', 'members', 'space'];

export function AdminRooms({ version, onChanged, onOpenUser }: { version: number; onChanged: () => void; onOpenUser: (userId: string) => void }) {
  const t = useT();
  const token = useApp((s) => s.token);
  const date = useDateText();
  const press = useLongPress();
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<AdminRoomSort>('recent');
  const [page, setPage] = useState<{ rooms: AdminRoomRow[]; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [menu, setMenu] = useState<{ anchor: MenuAnchor; room: AdminRoomRow } | null>(null);
  /** a lista da tela: muda a cada leitura do começo (outra busca, outra ordem) */
  const listRef = useRef(0);

  // a busca sai quando a pessoa para de digitar
  useEffect(() => {
    const timer = window.setTimeout(() => setSearch(query.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    listRef.current += 1;
    api.adminRooms(token, { q: search, sort, offset: 0 }).then(
      (result) => {
        if (!alive) return;
        setPage(result);
        setError(null);
      },
      (failure: unknown) => {
        if (alive) setError(describeError(failure));
      },
    );
    return () => {
      alive = false;
    };
  }, [token, search, sort, version]);

  const loadMore = async () => {
    if (!token || !page) return;
    const list = listRef.current;
    setLoadingMore(true);
    try {
      const next = await api.adminRooms(token, { q: search, sort, offset: page.rooms.length });
      // a lista mudou enquanto a página carregava: ela é de outra busca
      if (list !== listRef.current) return;
      setPage((current) => (current ? { rooms: [...current.rooms, ...next.rooms.filter((room) => !current.rooms.some((have) => have.id === room.id))], total: next.total } : next));
    } catch (failure) {
      toast(describeError(failure), 'error');
    } finally {
      setLoadingMore(false);
    }
  };

  const remove = async (room: AdminRoomRow) => {
    if (!token) return;
    // digitar o nome evita apagar a mesa errada num clique
    const typed = await askText({ title: t('admin.rooms.deleteTitle', { name: room.name }), label: t('admin.rooms.deleteLabel', { name: room.name }), maxLength: 60, action: t('admin.rooms.delete') });
    if (typed === null) return;
    if (typed.toLocaleLowerCase() !== room.name.trim().toLocaleLowerCase()) {
      toast(t('admin.rooms.deleteMismatch'), 'error');
      return;
    }
    try {
      await api.adminDeleteRoom(token, room.id, typed);
      toast(t('admin.rooms.deleteDone', { name: room.name }), 'success');
      onChanged();
    } catch (failure) {
      toast(describeError(failure), 'error');
    }
  };

  const entries = (room: AdminRoomRow): MenuEntry[] => [
    { kind: 'title', label: room.name },
    { label: t('admin.rooms.openOwner'), onSelect: () => onOpenUser(room.owner.id) },
    { kind: 'separator' },
    { label: t('admin.rooms.delete'), danger: true, onSelect: () => void remove(room) },
  ];

  return (
    <section className="rd-adminRooms" aria-label={t('admin.section.rooms')}>
      <div className="rd-adminUsers__tools">
        <label className="rd-searchBox rd-adminUsers__search">
          <Icon name="search" size={16} />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('admin.rooms.search')} aria-label={t('admin.rooms.search')} />
        </label>
        <ComboBox value={sort} onChange={setSort} aria-label={t('admin.rooms.sort')} options={SORTS.map((s) => ({ value: s, label: t(`admin.rooms.sort.${s}`) }))} />
      </div>
      {error ? (
        <p className="rd-adminUsers__error" role="alert">
          {error}
        </p>
      ) : null}
      {!page && !error ? (
        <div className="rd-placeholder">
          <Spinner size={28} />
        </div>
      ) : null}
      {page ? (
        <div className="rd-adminUsers__body">
          <p className="rd-adminUsers__count">{t('admin.rooms.count', { n: page.total })}</p>
          {page.rooms.length ? (
            <div className="rd-adminTable rd-adminTable--rooms" role="table" aria-label={t('admin.section.rooms')}>
              <div className="rd-adminTable__row rd-adminTable__row--head" role="row">
                {(['room', 'owner', 'members', 'online', 'files', 'created', 'last'] as const).map((col) => (
                  <span key={col} role="columnheader" className={`rd-adminTable__${col}`}>
                    {t(`admin.rooms.col.${col}`)}
                  </span>
                ))}
                <span role="columnheader" />
              </div>
              {page.rooms.map((room) => (
                <div key={room.id} className="rd-adminTable__row" role="row" aria-label={room.name} {...press.bind((anchor) => setMenu({ anchor, room }))}>
                  <span role="cell" className="rd-adminTable__room">
                    <span className="rd-adminTable__name">{room.name}</span>
                    {room.isPrivate ? <span className="rd-adminBadge">{t('admin.rooms.private')}</span> : null}
                    {room.hasPassword ? <span className="rd-adminBadge">{t('admin.rooms.password')}</span> : null}
                  </span>
                  <span role="cell" className="rd-adminTable__owner">
                    <button type="button" className="rd-adminLink" onClick={() => onOpenUser(room.owner.id)}>
                      {room.owner.displayName}
                    </button>
                  </span>
                  <span role="cell" className="rd-adminTable__members" title={t('admin.rooms.col.members')}>
                    {room.members}
                  </span>
                  <span role="cell" className={`rd-adminTable__online${room.online ? ' is-online' : ''}`} title={t('admin.rooms.col.online')}>
                    {room.online}
                  </span>
                  <span role="cell" className="rd-adminTable__files" title={t('admin.rooms.col.files')}>
                    {t('admin.rooms.filesValue', { n: room.files, size: formatBytes(room.bytes) })}
                  </span>
                  <span role="cell" className="rd-adminTable__created">
                    {date(room.createdAt)}
                  </span>
                  <span role="cell" className="rd-adminTable__last">
                    {room.lastMessageAt === null ? t('admin.rooms.never') : date(room.lastMessageAt)}
                  </span>
                  <span role="cell" className="rd-adminTable__actions">
                    <button
                      type="button"
                      className="rd-iconBtn rd-adminTable__more"
                      aria-label={t('admin.rooms.more', { name: room.name })}
                      onClick={(e) => {
                        const r = e.currentTarget.getBoundingClientRect();
                        setMenu({ anchor: { x: r.left, y: r.bottom }, room });
                      }}
                    >
                      ⋯
                    </button>
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <EmptyState icon="sheet" title={t('admin.rooms.none')} />
          )}
          {page.rooms.length < page.total ? (
            <Button className="rd-adminUsers__more" busy={loadingMore} onClick={() => void loadMore()}>
              {t('admin.loadMore')}
            </Button>
          ) : null}
        </div>
      ) : null}
      {menu ? <ContextMenu anchor={menu.anchor} label={menu.room.name} entries={entries(menu.room)} onClose={() => setMenu(null)} /> : null}
    </section>
  );
}
